import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
  fullWidth?: boolean;
}

const Input: React.FC<InputProps> = ({
  className,
  label,
  helperText,
  error,
  leftIcon,
  rightIcon,
  containerClassName,
  fullWidth = false,
  id,
  type,
  ...props
}) => {
  const inputId = id || `input-${Math.random().toString(36).substring(2, 9)}`;
  const inputRef = React.useRef<HTMLInputElement>(null);
  
  const handleContainerClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      
      // For date and time inputs, open the native picker
      if (type === 'date' || type === 'time') {
        inputRef.current.click();
      }
    }
  };
  
  return (
    <div className={twMerge('mb-4', fullWidth && 'w-full', containerClassName)}>
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          {label}
        </label>
      )}
      
      <div 
        className="relative cursor-text"
        onClick={handleContainerClick}
      >
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            {leftIcon}
          </div>
        )}
        
        <input
          ref={inputRef}
          id={inputId}
          type={type}
          className={twMerge(
            'block rounded-lg border px-4 py-2 w-full text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow',
            error ? 'border-error-500' : 'border-neutral-300',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            fullWidth && 'w-full',
            className
          )}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {rightIcon}
          </div>
        )}
      </div>
      
      {(error || helperText) && (
        <p className={twMerge(
          'mt-1 text-sm',
          error ? 'text-error-500' : 'text-neutral-500'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  );
};

export default Input;